import { action, computed, observable, makeObservable } from 'mobx'
import { required, validationContext } from 'validx'
import { task } from 'mobx-task'

import { BoolPromptInputEditorViewModel } from './Bool/BoolPromptInputEditorViewModel'

import { PromptType } from 'jobs/prompts/prompt-types'
import { PromptDesignee } from 'jobs/prompts/Prompt'
import Job from 'jobs/Job'

import { Id } from 'types/definitions'
import { lengthBetween } from 'utils/validation-utils'
import { AlertFactory, AlertState } from 'alerts/Alert'
import ModalViewModel from 'infra/ModalViewModel'
import PromptStore from 'jobs/prompts/PromptStore'
import DocumentUploadInputEditorViewModel from './Documents/DocumentUploadInputEditorViewModel'
import { TextPromptInputEditorViewModel } from './Text/TextPromptInputEditorViewModel'

/**
 * Represents the shape of prompt type options.
 */
export interface IPromptOption {
  displayName: string
  promptType: PromptType
}

/**
 * View Model for the Create Prompt Dialog.
 */
export default class CreatePromptDialogViewModel extends ModalViewModel {
  /**
   * Holds the maximum lengths for each field.
   */
  private readonly fieldLengths = {
    title: { min: 1, max: 100 },
    description: { min: 1, max: 280 },
  } as const

  /**
   *  Options for Prompt selection.
   */
  options: IPromptOption[] = [
    { displayName: 'Yes / No', promptType: PromptType.Bool },
    { displayName: 'Document Upload', promptType: PromptType.DocumentUpload },
    { displayName: 'Text', promptType: PromptType.Text },
  ]

  /**
   * The title of the prompt.
   */
  @observable
  title = ''

  /**
   * The description of the prompt.
   */
  @observable
  description = ''

  /**
   * The type of prompt. Can be a Yes/No Prompt, a Document Upload Prompt,
   * etc.
   */
  @observable
  promptType: PromptType = PromptType.Bool

  /**
   * Registry of detail view models for each possible type of prompt question.
   */
  readonly detailsViewModels = {
    [PromptType.Bool]: new BoolPromptInputEditorViewModel(),
    [PromptType.DocumentUpload]: new DocumentUploadInputEditorViewModel(),
    [PromptType.Text]: new TextPromptInputEditorViewModel(),
  } as const

  /**
   * Validation context for this view model.
   */
  validation = validationContext(this, {
    title: [
      required('A prompt title is required.'),
      lengthBetween(
        this.fieldLengths.title.min,
        this.fieldLengths.title.max,
        `A prompt title must be between ${this.fieldLengths.title.min} and ${this.fieldLengths.title.max} characters.`,
      ),
    ],
    description: [
      required('A prompt description is required.'),
      lengthBetween(
        this.fieldLengths.description.min,
        this.fieldLengths.description.max,
        `A prompt description must be between ${this.fieldLengths.description.min} and ${this.fieldLengths.description.max} characters.`,
      ),
    ],
  })

  constructor(
    private job: Job,
    private newAlert: AlertFactory,
    private promptStore: PromptStore,
  ) {
    super()

    makeObservable(this)

    this.close$.subscribe(this.reset)
  }

  /**
   * Computes the number of remaining characters for each field and creates a
   * text-based indicator using that result. For any field, if the number of
   * characters within it is `0`, `undefined` is returned. The view can use this
   * to choose not to display this indicator when there's no text in the field.
   */
  @computed
  get charsRemainingIndicators() {
    const makeIndicator = (max: number, current: number) =>
      current !== 0 ? `${max - current} characters remaining` : undefined

    return {
      title: makeIndicator(this.fieldLengths.title.max, this.title.length),
      description: makeIndicator(
        this.fieldLengths.description.max,
        this.description.length,
      ),
    }
  }

  /**
   * The currently selected prompt option.
   */
  @computed
  get option() {
    return this.options.find((o) => o.promptType === this.promptType)
  }

  /**
   * Sets the prompt type.
   */
  @action.bound
  setPromptOption(promptOption?: IPromptOption) {
    if (promptOption) {
      this.promptType = promptOption.promptType
    }
  }

  /**
   * Indicates whether this view model and the details view model for the active
   * prompt type are both valid.
   */
  @action.bound
  validate() {
    const isThisViewModelValid = this.validation.reset().validate().isValid
    const isActiveInputEditorViewModelValid =
      this.detailsViewModels[this.promptType].validate()

    return isThisViewModelValid && isActiveInputEditorViewModelValid
  }

  /**
   * Sends the specified prompt based on `promptType`.
   */
  sendPrompt = task.resolved(async () => {
    if (!this.validate()) {
      return
    }

    await this.promptStore.sendPrompt({
      jobId: Id.assert(this.job.id),
      title: this.title,
      description: this.description,
      promptInput: this.detailsViewModels[this.promptType].toInput(),
      designee: PromptDesignee.Client,
    })

    this.newAlert()
      .withState(AlertState.Success)
      .saying('Success', 'Your Prompt has been sent.')
      .autoDismiss()
      .show()

    this.close()
  })

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  private reset() {
    this.title = ''
    this.description = ''

    this.validation.reset()
    this.sendPrompt.reset()

    this.promptType = PromptType.Bool
    Object.values(this.detailsViewModels).forEach((vm) => vm.reset())
  }
}
